import type { SoftphoneCollapsibleCallItemProps } from './index.types';
import React from 'react';
import { cn } from '~/lib/utils';
import { baseClasses } from '../index.styles';

/**
 * SoftphoneCollapsibleCallItem — Generic item component for use inside SoftphoneCollapsibleCallGroup.
 *
 * Renders a button with an optional left icon, display name, and optional right content.
 * Supports forwardRef so SoftphoneCollapsibleCallGroup can inject refs via React.cloneElement.
 * When showHoverState is true (default), applies hover/focus-visible background styles.
 */
export const SoftphoneCollapsibleCallItem = React.memo(
  React.forwardRef<HTMLButtonElement, SoftphoneCollapsibleCallItemProps>(
    (
      {
        displayName,
        rightContent,
        leftIcon,
        onClick,
        ariaLabel,
        showHoverState = true,
        className,
        testID,
        tabIndex,
        'data-focused': dataFocused,
        ...htmlProps
      },
      ref
    ): React.ReactElement => {
      return (
        <button
          ref={ref}
          type="button"
          aria-label={ariaLabel}
          tabIndex={tabIndex}
          data-focused={dataFocused}
          data-testid={testID}
          className={cn(
            baseClasses,
            `h-[55px] px-[16px] py-[8px] focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-inset`,
            showHoverState && `hover:bg-accent focus-visible:bg-accent`,
            className
          )}
          onClick={onClick}
          {...htmlProps}
        >
          <div className="flex items-center gap-2 text-left flex-1 min-w-0">
            {leftIcon && <div className="flex-shrink-0">{leftIcon}</div>}
            <span className="text-[14px] text-foreground font-semibold truncate leading-[19px]">
              {displayName}
            </span>
          </div>

          {rightContent && <div className="flex-shrink-0">{rightContent}</div>}
        </button>
      );
    }
  )
);

SoftphoneCollapsibleCallItem.displayName = 'SoftphoneCollapsibleCallItem';
